<template>
  <div class="dropdown-parent-container">
    <button
      type="button"
      title="Select a Saved Chart"
      :id="CHARTS_DROPDOWN_MENU_TOGGLE_BUTTON_ID"
      class="dropdown-menu-toggle-arrow-icon-button sc3-button sc3-button-fixed-height sc3-button-white-and-dark-grey"
      :class="{
        'dropdown-menu-toggle-arrow-icon-button-open sc3-button-active': isOpen,
        'no-chart-selected': !selectedChartInSelectedChartList
      }"
      ref="chartsDropdownMenuButton"
      @click="toggleDropdown()"
    >
      {{ selectedChartName }}
      &nbsp;
      <i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="chartsDropdownMenuButton ? [chartsDropdownMenuButton] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="chartsDropdownMenuButton"
      :include-header-divider="false"
    >
      <template v-slot:header>
        <div class="dropdown-header-container">
          <span class="dropdown-menu-title">YOUR SAVED CHARTS</span>
          <span
            v-show="!isChartListLoading && !hasFailedToLoadCharts && selectedChartList && !isSelectedChartListEmpty"
            class="dropdown-menu-title-right-corner-ui-container"
          >
            <SCThreeCheckbox
              id="charts-dropdown-menu-load-symbol-only-checkbox"
              label-text="Load Symbol Only"
              :checkbox-value="isLoadSymbolOnlyEnabled"
              @update:checkbox-value="setIsLoadSymbolOnlyEnabled"
              :is-small-font="true"
              :is-circle="true"
              :is-single-spaced="true"
              :is-lato-font="true"
            />
          </span>
        </div>
        <div class="dropdown-divider"></div>
        <div class="chartlist-search-box-container">
          <i class="fas fa-fw fa-search textbox-search-icon"></i>
          <input
            type="text"
            class="sc3-text-input sc3-text-input-inline chartlist-search-box"
            title="Search"
            placeholder="Search"
            v-model="chartsSearchQuery"
            autocomplete="off"
          />
          <button
            type="button"
            class="clear-search-query-button"
            title="Clear Charts Search"
            @click="clearSearchQuery"
            v-show="chartsSearchQuery"
          >
            <i class="fa fa-times-circle"></i>
          </button>
        </div>
      </template>
      <template v-slot:body>
        <div v-if="isChartListLoading" class="dropdown-loading-body">
          <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
        </div>
        <div v-else-if="hasFailedToLoadCharts" class="dropdown-message-body">
          <span class="dropdown-message-small">Failed to load charts.</span>
        </div>
        <div v-else-if="!selectedChartList" class="dropdown-message-body">
          <span>Please select a ChartList from the menu above.</span>
        </div>
        <div v-else-if="isSelectedChartListEmpty" class="dropdown-message-body">
          <span>It looks like this {{ selectedChartList.isReadonly ? 'read-only' : '' }} ChartList is&nbsp;empty!</span>
          <br />
          <span class="dropdown-message-small">
            Save and organize the charts you're watching
            <br />
            with your own&nbsp;ChartLists.
          </span>
          <br v-if="!selectedChartList.isReadonly" />
          <a
            v-if="!selectedChartList.isReadonly"
            :href="`/def/servlet/Favorites.CServlet?obj=${chartCode},${selectedChartList.listNum}&cmd=show&disp=c`"
            class="sc3-button sc3-button-green"
          >Add Symbols</a>
        </div>
        <div v-else-if="isChartsSearchQueryFilterEmpty" class="dropdown-message-body">
          <span>It looks like you don't have any charts<br>in this ChartList that match your search.</span>
        </div>
        <ul v-else>
          <li
            v-for="chart in filteredCharts"
            :key="chart.recid"
            :class="{ 'selected-dropdown-option': selectedChartInSelectedChartList?.recid === chart.recid }"
          >
            <button
              type="button"
              class="ellipsis-overflow"
              title="Load Chart"
              @click="[closeDropdown(), loadSimpleFrontendChart(chart, { shouldScrollToChartOnSuccess: false, shouldEnableArrowHotkeys: true })]"
              v-sanitize-html="chart.name"
            ></button>
            <button
              type="button"
              title="Edit Chart Name"
              class="icon-button"
              :class="{ 'icon-on-row-hover-only': selectedChartInSelectedChartList?.recid !== chart.recid }"
              @click="[closeDropdown(), editChartName(chart)]"
              :disabled="selectedChartList.isReadonly"
            ><i class="fas fa-fw fa-pencil-alt blue-icon"></i></button>
            <button
              type="button"
              title="Delete Chart"
              class="icon-button"
              :class="{ 'icon-on-row-hover-only': selectedChartInSelectedChartList?.recid !== chart.recid }"
              @click="[closeDropdown(), deleteChart(chart)]"
              :disabled="selectedChartList.isReadonly"
            ><i class="fas fa-fw fa-trash-alt red-icon"></i></button>
          </li>
        </ul>
      </template>
      <template v-slot:footer v-if="selectedChartList && !isSelectedChartListEmpty && !hasFailedToLoadCharts && !isChartListLoading">
        <div class="apply-chartstyle-to-all-container">
          <button
            class="sc3-button sc3-button-extra-small sc3-button-grey"
            type="button"
            @click="applyChartStyleToAll()"
            :disabled="selectedChartList.isReadonly"
          >Apply ChartStyle To All</button>
        </div>
      </template>
    </DropdownMenu>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, useCssVars, watch } from 'vue';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { useLoadSymbolOnly } from '@/composables/useLoadSymbolOnly';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useModals } from '@/composables/useModals';
  import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import { SimpleFrontendChart } from '@/interfaces/chart';
  import { HEIGHT_OF_EACH_DROPDOWN_ELEMENT, MAX_AMOUNT_OF_DROPDOWN_ELEMENTS_TO_SHOW } from '@/enums/upper-bar-dropdown-constraints';
  import { CHARTS_DROPDOWN_MENU_TOGGLE_BUTTON_ID } from '@/enums/html-ids';

  const { isLoggedIn, chartCode } = useAuthentication();
  const {
    selectedChartList,
    selectedChartListCharts,
    selectedChartInSelectedChartList
  } = useWorkbenchSelections();
  const { isLoadSymbolOnlyEnabled, setIsLoadSymbolOnlyEnabled } = useLoadSymbolOnly();
  const { addYellowActionResultMessage } = useActionResultMessages();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const { loadSimpleFrontendChart } = useLoadChartsAndChartStyles();
  const {
    launchCreateAccountModal,
    launchDeleteChartModal,
    launchEditChartNameModal,
    launchApplyChartStyleToChartsModal
  } = useModals();
  const chartsSearchQuery = ref('');
  const chartsDropdownMenuButton = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(chartsDropdownMenuButton, { windowResize: true });
  const filteredCharts = computed(() => {
    const sanitizedQuery = chartsSearchQuery.value.toLowerCase().trim();
    if (!sanitizedQuery) {
      return selectedChartListCharts.value;
    }
    return selectedChartListCharts.value.filter((chart) => {
      return chart.searchTags.map(tag => tag.toLowerCase()).some(tag => tag.includes(sanitizedQuery));
    });
  });
  const selectedChartName = computed(() => {
    if (!selectedChartInSelectedChartList.value?.name) {
      return '-- Select a Chart --';
    }

    return selectedChartInSelectedChartList.value.htmlSanitizedName;
  });
  const hasFailedToLoadCharts = computed(() => {
    return !selectedChartListCharts.value.length && !!selectedChartList.value && !selectedChartList.value.isAttemptingToLoadCharts && selectedChartList.value.hasFailedToLoadCharts;
  });
  const isSelectedChartListEmpty = computed(() => {
    return !selectedChartListCharts.value.length && !!selectedChartList.value && !selectedChartList.value.isAttemptingToLoadCharts && !selectedChartList.value.hasFailedToLoadCharts;
  });
  const isChartsSearchQueryFilterEmpty = computed(() => {
    return !filteredCharts.value.length && !!selectedChartList.value && !selectedChartList.value.isAttemptingToLoadCharts && !selectedChartList.value.hasFailedToLoadCharts;
  });
  const isChartListLoading = computed(() => {
    return !selectedChartListCharts.value.length && (!selectedChartList || (selectedChartList.value?.isAttemptingToLoadCharts ?? false));
  });

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  function clearSearchQuery() {
    chartsSearchQuery.value = '';
  }

  async function applyChartStyleToAll() {
    isOpen.value = false;

    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    const chartList = selectedChartList.value;

    if (!chartList) {
      window.sc3WorkbenchLogger.error(`Failed to launch the ApplyChartStyleToCharts modal from the ChartsDropdownMenu - selectedChartList is falsy: ${chartList}.`);

      addYellowActionResultMessage('Please select a ChartList.');
      return;
    }

    await loadChartsForChartList(chartList.recid, chartList.listNum, true);

    launchApplyChartStyleToChartsModal(chartList, chartList.charts);
  }

  function editChartName(chart: SimpleFrontendChart) {
    closeDropdown();

    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    if (!selectedChartList.value) {
      window.sc3WorkbenchLogger.error(`Failed to launch the EditChartName modal - selectedChartList is falsy: ${selectedChartList.value}.`);

      addYellowActionResultMessage('Please select a ChartList.');
      return;
    }

    if (selectedChartList.value.isReadonly) {
      window.sc3WorkbenchLogger.error(`Failed to launch the EditChartName modal - selectedChartList is readonly.`);

      addYellowActionResultMessage('We\'re sorry, but you\'re unable to modify this read-only ChartList or its charts.');
      return;
    }

    launchEditChartNameModal(chart.recid);
  }

  function deleteChart(chart: SimpleFrontendChart) {
    closeDropdown();

    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    if (!selectedChartList.value) {
      window.sc3WorkbenchLogger.error(`Failed to launch the DeleteChart modal - selectedChartList is falsy: ${selectedChartList.value}.`);

      addYellowActionResultMessage('Please select a ChartList.');
      return;
    }

    if (selectedChartList.value.isReadonly) {
      window.sc3WorkbenchLogger.error(`Failed to launch the DeleteChart modal - selectedChartList is readonly.`);

      addYellowActionResultMessage('We\'re sorry, but you\'re unable to modify this read-only ChartList or its charts.');
      return;
    }

    launchDeleteChartModal(chart.recid);
  }

  watch(isOpen, (newOpenValue) => {
    if (!newOpenValue) {
      chartsDropdownMenuButton.value?.blur();
      clearSearchQuery();
    }
  });

  useCssVars(() => {
    return {
      'absolute-max-dropdown-height': `${HEIGHT_OF_EACH_DROPDOWN_ELEMENT * MAX_AMOUNT_OF_DROPDOWN_ELEMENTS_TO_SHOW}px`,
      'screen-size-max-dropdown-height': `${dropdownHeight.value}px`
    };
  });
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
    width: 100%;
  }

  .dropdown-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-weight: 900;
    color: #707f8f;
    text-align: left;
    padding: 2px 10px;
    margin: 0px;
    gap: 10px;
  }

  .apply-chartstyle-to-all-container {
    display: flex;
    justify-content: center;
  }

  div.chartlist-search-box-container {
    position: relative;
    padding: 4px;
  }

  div.chartlist-search-box-container i.textbox-search-icon {
    position: absolute;
    transform: translate(0px, -50%);
    top: 50%;
    font-size: 12px;
    color: #707f8f;
    left: 4px;
    padding: 0px 6px;
  }

  div.chartlist-search-box-container input.chartlist-search-box {
    width: 100%;
    margin: 0px;
    padding: 0px 20px;
    height: 25px;
    text-overflow: ellipsis;
    border-width: 1px;
  }

  div.chartlist-search-box-container button.clear-search-query-button {
    position: absolute;
    transform: translate(0px, -50%);
    top: 50%;
    font-size: 12px;
    color: #707f8f;
    right: 4px;
    padding: 0px 6px;
    cursor: pointer;
    background: none;
    border: none;
    transition: all 0.35s ease;
  }

  div.chartlist-search-box-container button.clear-search-query-button:hover {
    color: #a9b2bc;
  }

  ul {
    max-height: min(
      var(--absolute-max-dropdown-height),
      var(--screen-size-max-dropdown-height)
    );
  }

  .dropdown-message-body {
    padding: 10px;
    font-weight: bold;
    text-align: center;
  }

  .dropdown-message-body .dropdown-message-small {
    font-size: 14px;
  }

  .dropdown-loading-body {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
  }

  #charts-dropdown-menu-toggle-button { /* "charts-dropdown-menu-toggle-button" matches the value of the CHARTS_DROPDOWN_MENU_TOGGLE_BUTTON_ID html-id enum */
    font-size: 12px;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  #charts-dropdown-menu-toggle-button.no-chart-selected { /* "charts-dropdown-menu-toggle-button" matches the value of the CHARTS_DROPDOWN_MENU_TOGGLE_BUTTON_ID html-id enum */
    color: #707f8f;
  }
</style>
